import React, { useState } from 'react';
import { Image, ScrollView, StyleSheet, TouchableOpacity } from 'react-native';

import { ScopedSignupModal } from '@components/Modals/ScopedSignupModal.web';
import { Box, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import {
  Invitation,
  useGetPublicProjectQuery,
  useInvitationDetailsQuery,
} from '@graphql/generated';
import useColors from '@hooks/useColors';
import { useAuthContext } from '@src/context/authContext';
import Images from '@themes/images';
import theme from '@themes/theme';
import { resolveInvitationSignupPrefill } from '@utils/invitationSignupPrefill';
import {
  APP_STORE_URL,
  buildInviterDeepLink,
  detectAppInstallation,
  isMobileBrowser,
} from '@utils/mobileDeepLink';
import { setPendingInviter } from '@utils/pendingInviterChat';

import { getAvatarColor, getInitials } from './projectInvitationPreviewShared';
import { sharedStyleDefs } from './projectPreviewShared';

/* ─── helpers ─────────────────────────────────────────────────────────────── */

type AvatarData = { cdnBaseUrl?: string | null; path?: string | null };

export const buildAvatarUri = (avatar?: AvatarData | null): string | null =>
  avatar?.cdnBaseUrl && avatar?.path
    ? `${avatar.cdnBaseUrl}${avatar.path}`
    : null;

const checkInvalidInvitation = (
  loading: boolean,
  queryError: unknown,
  project: unknown,
  projectId: string | undefined,
  invitationType?: string | null
): boolean =>
  !loading &&
  (!!queryError || !project || !projectId || invitationType == null);

const CTA_COPY = {
  email: { button: 'Join This Project' },
  link: { button: 'Request to Join' },
} as const;

/* ─── Sidebar nav item (disabled) ───────────────────────────────────────── */

type DisabledNavItemProps = {
  iconName: React.ComponentProps<typeof Icon>['name'];
  width: number;
};

const DisabledNavItem: React.FC<DisabledNavItemProps> = ({
  iconName,
  width,
}) => (
  <Box style={styles.sidebarNavItem}>
    <Icon name={iconName} width={24} height={24} color='grey02' />
    <Box style={[styles.navLabelSkeleton, { width: width }]} />
  </Box>
);

/* ─── Tab item ───────────────────────────────────────────────────────────── */

type TabItemProps = {
  label: string;
  iconName: React.ComponentProps<typeof Icon>['name'];
  active?: boolean;
  badge?: number | null;
};

const TabItem: React.FC<TabItemProps> = ({
  label,
  iconName,
  active = false,
  badge,
}) => (
  <Box style={styles.tabItemWrap}>
    <Box style={styles.tabItemContent}>
      <Icon name={iconName} variant='s' color='grey04' />
      <Text style={styles.tabItemText}>{label}</Text>
      {badge != null && (
        <Box style={styles.tabBadge}>
          <Text style={styles.tabBadgeText}>{badge}</Text>
        </Box>
      )}
    </Box>
    <Box style={[styles.tabUnderbar, !active && styles.tabUnderbarHidden]} />
  </Box>
);

/* ─── main screen ─────────────────────────────────────────────────────────── */

const ProjectInvitationPreviewScreenWeb: React.FC = () => {
  const { getMappedColor } = useColors();
  const [joinModalVisible, setJoinModalVisible] = useState(false);

  const { token: authToken } = useAuthContext();
  const token = window.sessionStorage.getItem('invitation_token') ?? '';
  const projectId =
    window.sessionStorage.getItem('task_or_project_id') ?? undefined;

  const {
    data,
    loading,
    error: queryError,
  } = useGetPublicProjectQuery({
    variables: { id: projectId ?? '', token: token || undefined },
    skip: !projectId,
    fetchPolicy: 'network-only',
  });

  const {
    data: inviteDetailsData,
    loading: inviteDetailsLoading,
    error: inviteDetailsError,
  } = useInvitationDetailsQuery({
    variables: { token, type: Invitation.Project, entityId: projectId },
    skip: !token,
    fetchPolicy: 'network-only',
  });

  const rawProject = data?.getPublicProject ?? null;
  const pubProject =
    rawProject?.__typename === 'PublicProject' ? rawProject : null;

  const inviteDetails = inviteDetailsData?.invitationDetails ?? null;
  const invitationStatus = inviteDetails?.invitationStatus ?? null;
  const isExpired =
    invitationStatus != null
      ? invitationStatus === 'expired'
      : inviteDetails != null &&
        !inviteDetails.autoJoin &&
        !inviteDetails.isLinkInvite;
  const isDefinitelyInvalid = invitationStatus === 'invalid';

  const isMobile = isMobileBrowser();

  const handleLogin = () => {
    window.location.href = `${window.location.origin}/log-in`;
  };

  /* ── Loading ────────────────────────────────────────────────────────────── */

  if (loading || inviteDetailsLoading) {
    return (
      <Box style={styles.centered}>
        <ActivityIndicatorLoading />
      </Box>
    );
  }

  /* ── Network / invite-details error ────────────────────────────────────── */

  if (inviteDetailsError) {
    return (
      <Box style={[styles.centered, { paddingHorizontal: theme.spacing.l }]}>
        <Text style={styles.errorTitle}>Could not load invitation</Text>
        <Text style={styles.errorBody}>
          Please check your connection and try again.
        </Text>
        <Box style={{ marginTop: theme.spacing.xl, width: '100%' }}>
          <Button
            variant='primary'
            fullWidth
            onPress={() => window.location.reload()}>
            <Text style={styles.buttonLabel}>Try again</Text>
          </Button>
        </Box>
      </Box>
    );
  }

  /* ── Invalid invitation ─────────────────────────────────────────────────── */

  if (
    !isExpired &&
    (isDefinitelyInvalid ||
      checkInvalidInvitation(
        loading,
        queryError,
        rawProject,
        projectId,
        pubProject?.invitationType
      ))
  ) {
    return (
      <Box style={[styles.centered, { paddingHorizontal: theme.spacing.l }]}>
        <Text style={styles.errorTitle}>Invalid Invitation</Text>
        <Text style={styles.errorBody}>
          This invitation link is invalid or has expired.
        </Text>
        <Box style={{ marginTop: theme.spacing.xl, width: '100%' }}>
          <Button variant='primary' fullWidth onPress={handleLogin}>
            <Text style={styles.buttonLabel}>Create account</Text>
          </Button>
        </Box>
      </Box>
    );
  }

  /* ── Expired invitation ─────────────────────────────────────────────────── */

  if (isExpired) {
    const expiredProject = inviteDetails?.project ?? null;
    const expiredInviterName = inviteDetails?.invitedByName ?? null;
    const expiredRoleRaw = inviteDetails?.invitationRole ?? 'Member';
    const expiredRoleLabel =
      expiredRoleRaw.charAt(0).toUpperCase() +
      expiredRoleRaw.slice(1).toLowerCase();
    const teamAvatarUri = buildAvatarUri(expiredProject?.team?.avatarFile);
    // TA-10943: an expired invite is still a request to join, but expiring does
    // not turn an email invitation into a link one — keep the invited address
    // prefilled and locked.
    const expiredSignupPrefill = resolveInvitationSignupPrefill(inviteDetails, {
      isExpired: true,
    });

    return (
      <Box style={styles.container}>
        <Box style={styles.topBar}>
          <Icon name='Logo' width={100} height={28} color='textPrimary' />
          {authToken ? (
            <Box style={styles.topBarAvatar}>
              <Icon name='User' width={18} height={18} color='grey05' />
            </Box>
          ) : (
            <TouchableOpacity onPress={() => setJoinModalVisible(true)}>
              <Text style={styles.topBarSignUp}>Sign Up</Text>
            </TouchableOpacity>
          )}
        </Box>

        <ScrollView
          style={styles.scroll}
          contentContainerStyle={[styles.content, styles.expiredScrollContent]}
          showsVerticalScrollIndicator={false}>
          <Box style={styles.expiredHeaderBlock}>
            <Box
              style={[
                styles.expiredIconCircle,
                isMobile
                  ? styles.expiredIconCircleMobile
                  : styles.expiredIconCircleDesktop,
              ]}>
              <Icon
                name='Clock'
                width={isMobile ? 32 : 48}
                height={isMobile ? 32 : 48}
                color='alertRed'
              />
            </Box>
            <Text
              style={[
                styles.expiredTitle,
                isMobile
                  ? styles.expiredTitleMobile
                  : styles.expiredTitleDesktop,
              ]}>
              Invitation has expired
            </Text>
            <Text style={styles.expiredSubtitle}>
              You can still request to join this project.
            </Text>
          </Box>

          {!!expiredProject && (
            <Box style={styles.expiredProjectCard}>
              <Box style={styles.expiredProjectInfoBlock}>
                <Text style={styles.expiredProjectName}>
                  {expiredProject.name}
                </Text>
                {!!expiredProject.address && (
                  <Text style={styles.expiredProjectAddress} numberOfLines={1}>
                    {expiredProject.address}
                  </Text>
                )}
                <Box style={styles.expiredProjectMeta}>
                  {!!expiredProject.team?.name && (
                    <Box style={styles.expiredMetaItem}>
                      {teamAvatarUri ? (
                        <Image
                          source={{ uri: teamAvatarUri }}
                          style={styles.expiredTeamAvatar}
                        />
                      ) : (
                        <Box style={styles.expiredTeamIconBox}>
                          <Icon
                            name='TT'
                            width={10}
                            height={10}
                            color='white'
                          />
                        </Box>
                      )}
                      <Text style={styles.expiredMetaText} numberOfLines={1}>
                        {expiredProject.team?.name}
                      </Text>
                    </Box>
                  )}
                  {!!expiredInviterName && (
                    <Box style={styles.expiredMetaItem}>
                      <Box style={styles.expiredInviterDot}>
                        <Text style={styles.expiredInviterInitials}>
                          {getInitials(expiredInviterName)}
                        </Text>
                      </Box>
                      <Text style={styles.expiredMetaText} numberOfLines={1}>
                        {expiredInviterName}
                      </Text>
                    </Box>
                  )}
                </Box>
              </Box>

              {!!expiredProject.description && (
                <Box style={styles.expiredSection}>
                  <Text style={styles.expiredSectionTitle}>Description</Text>
                  <Text style={styles.expiredSectionBody}>
                    {expiredProject.description}
                  </Text>
                </Box>
              )}

              <Box style={styles.expiredSection}>
                <Text style={styles.expiredSectionTitle}>Total Members</Text>
                <Box style={styles.expiredMemberRow}>
                  <Icon
                    name='Users'
                    width={isMobile ? 12 : 16}
                    height={isMobile ? 12 : 16}
                    color='textSecondary'
                  />
                  <Text style={styles.expiredSectionBody}>
                    {expiredProject.usersCount} people working on this project
                  </Text>
                </Box>
              </Box>
            </Box>
          )}

          <TouchableOpacity
            style={styles.expiredJoinButton}
            onPress={() => setJoinModalVisible(true)}
            activeOpacity={0.85}>
            <Text style={styles.expiredJoinButtonLabel}>Request to Join</Text>
          </TouchableOpacity>

          <Text style={styles.expiredAdminHint}>
            Admin will receive your request after sign up
          </Text>

          <Box style={styles.expiredLoginRow}>
            <Text style={styles.expiredLoginPrompt}>Already on TaskTag? </Text>
            <TouchableOpacity onPress={handleLogin}>
              <Text style={styles.expiredLoginLink}>Log in</Text>
            </TouchableOpacity>
          </Box>
        </ScrollView>

        {joinModalVisible && (
          <ScopedSignupModal
            invitationType='project'
            entityName={expiredProject?.name ?? ''}
            entityAddress={expiredProject?.address ?? undefined}
            inviterName={expiredInviterName ?? ''}
            role={expiredRoleLabel}
            email={expiredSignupPrefill.email}
            invitationToken={token}
            entityId={projectId}
            isLinkInvite={expiredSignupPrefill.isLinkInvite}
            isEmailLocked={expiredSignupPrefill.isEmailLocked}
            usersCount={expiredProject?.usersCount ?? null}
            onClose={() => setJoinModalVisible(false)}
            onSuccess={() => {
              window.sessionStorage.setItem('invitation_token', '');
              window.sessionStorage.setItem('link_type', '');
              window.sessionStorage.setItem('task_or_project_id', '');
              if (projectId) {
                window.sessionStorage.setItem(
                  'pending_project_uuid',
                  projectId
                );
                window.sessionStorage.setItem(
                  'pending_request_sent',
                  projectId
                );
              }
              setJoinModalVisible(false);
            }}
          />
        )}
      </Box>
    );
  }

  /* ── Valid project — derive display data ────────────────────────────────── */

  const { name, description, address, usersCount } = rawProject!;
  const isEmailInvite =
    inviteDetails != null
      ? !inviteDetails.isLinkInvite
      : pubProject?.invitationType === 'email';
  const rawRole = isEmailInvite
    ? (inviteDetails?.invitationRole ?? pubProject?.inviteeRole ?? 'Member')
    : 'Viewer';
  const roleLabel =
    rawRole.charAt(0).toUpperCase() + rawRole.slice(1).toLowerCase();
  const resolvedInviterName =
    pubProject?.inviterName ??
    (pubProject?.members ?? []).find((m) => m.role === 'OWNER')?.name ??
    null;
  const cta = CTA_COPY[isEmailInvite ? 'email' : 'link'];

  // Mobile — additional derived data
  const members = pubProject?.members ?? [];
  const cardBgColor: string = pubProject?.color
    ? getMappedColor(pubProject.color)
    : theme.colors.purple;
  const projectIconName = (
    pubProject?.icon && pubProject.icon in Images
      ? pubProject.icon
      : 'IconStart22'
  ) as React.ComponentProps<typeof Icon>['name'];
  const teamAvatarUri = buildAvatarUri(pubProject?.team?.avatarFile);
  const inviterMember =
    members.find((m) => m.role === 'OWNER') ?? members[0] ?? null;
  const inviterAvatarUri = buildAvatarUri(inviterMember?.avatar);
  const inviterInitials = resolvedInviterName
    ? getInitials(resolvedInviterName)
    : '?';

  const handleJoinPress = () => setJoinModalVisible(true);

  const signupModal = joinModalVisible && (
    <ScopedSignupModal
      invitationType='project'
      entityName={name}
      entityAddress={address ?? undefined}
      inviterName={resolvedInviterName ?? ''}
      role={roleLabel}
      email={isEmailInvite ? (pubProject?.invitationEmail ?? '') : ''}
      invitationToken={token}
      entityId={projectId}
      isLinkInvite={!isEmailInvite}
      usersCount={usersCount}
      onClose={() => setJoinModalVisible(false)}
      onSuccess={() => {
        window.sessionStorage.setItem('invitation_token', '');
        window.sessionStorage.setItem('link_type', '');
        window.sessionStorage.setItem('task_or_project_id', '');
        if (projectId) {
          window.sessionStorage.setItem('pending_project_uuid', projectId);
        }
        if (!isEmailInvite && projectId) {
          window.sessionStorage.setItem('pending_request_sent', projectId);
        }
        if (!isMobileBrowser() && isEmailInvite) {
          // TA-10891 #21: app-stack.web opens the inviter's 1:1 chat once
          // the new member lands in the app.
          setPendingInviter(inviteDetails?.invitedBy?.id);
        }
        if (isMobileBrowser() && isEmailInvite) {
          // Carry the inviter's id into the native app so it can open a 1:1
          // chat with them right after the user logs in (handled in Chats.tsx).
          detectAppInstallation(
            buildInviterDeepLink(inviteDetails?.invitedBy?.id),
            APP_STORE_URL
          );
        }
        setJoinModalVisible(false);
      }}
    />
  );

  /* ── Mobile layout ──────────────────────────────────────────────────────── */

  if (isMobile) {
    return (
      <Box style={styles.container}>
        {/* Header */}
        <Box style={styles.topBar}>
          <Icon name='Logo' width={100} height={28} color='textPrimary' />
          {!authToken && (
            <TouchableOpacity onPress={handleJoinPress}>
              <Text variant='webSmall' style={styles.topBarSignUp}>
                Sign Up
              </Text>
            </TouchableOpacity>
          )}
        </Box>

        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.mobileContent}
          showsVerticalScrollIndicator={false}>
          {/* Purple project card */}
          <Box
            style={[styles.mobProjectCard, { backgroundColor: cardBgColor }]}>
            <Box style={styles.mobProjectTitleRow}>
              <Icon
                name={projectIconName}
                width={24}
                height={24}
                color='white'
              />
              <Text style={styles.mobProjectName} numberOfLines={2}>
                {name}
              </Text>
            </Box>
            {!!address && (
              <Box style={styles.mobProjectAddressRow}>
                <Text style={styles.mobProjectAddress} numberOfLines={1}>
                  {address}
                </Text>
              </Box>
            )}
            <Box style={styles.mobTagsRow}>
              {!!pubProject?.team?.name && (
                <Box style={styles.mobTag}>
                  <Box style={styles.mobTeamLogoBox}>
                    {teamAvatarUri ? (
                      <Image
                        source={{ uri: teamAvatarUri }}
                        style={styles.mobTeamLogoImg}
                      />
                    ) : (
                      <Icon
                        name='TTSquare'
                        width={24}
                        height={24}
                        marginTop='xxxs'
                        marginLeft='xxxs'
                      />
                    )}
                  </Box>
                  <Text style={styles.mobTagText} numberOfLines={1}>
                    {pubProject.team!.name}
                  </Text>
                </Box>
              )}
              {!!resolvedInviterName && (
                <Box style={styles.mobTag}>
                  <Box
                    style={[
                      styles.mobTagAvatarFallback,
                      { backgroundColor: getAvatarColor(0) },
                    ]}>
                    {inviterAvatarUri ? (
                      <Image
                        source={{ uri: inviterAvatarUri }}
                        style={styles.mobTagAvatar}
                      />
                    ) : (
                      <Box style={styles.mobTagAvatarFallback}>
                        <Text style={styles.mobTagAvatarInitials}>
                          {inviterInitials}
                        </Text>
                      </Box>
                    )}
                  </Box>
                  <Text style={styles.mobTagText} numberOfLines={1}>
                    {resolvedInviterName}
                  </Text>
                </Box>
              )}
            </Box>
          </Box>

          {/* Description card */}
          {!!description && (
            <Box style={styles.mobCard}>
              <Box style={styles.mobCardTitleRow}>
                <Text style={styles.mobCardTitle}>Description</Text>
                {description.length > 120 && (
                  <Text style={styles.mobSeeMore}>See More</Text>
                )}
              </Box>
              <Text style={styles.descBody} numberOfLines={4}>
                {description}
              </Text>
            </Box>
          )}

          {/* Black CTA card */}
          <Box style={styles.mobCtaCard}>
            <Box style={styles.mobCtaAvatarWrap}>
              {inviterAvatarUri ? (
                <Image
                  source={{ uri: inviterAvatarUri }}
                  style={styles.mobCtaAvatar}
                />
              ) : (
                <Box style={styles.mobCtaAvatarFallback}>
                  <Icon
                    name='UserPlus'
                    width={19}
                    height={19}
                    color='greenBrand'
                  />
                </Box>
              )}
            </Box>
            <Box style={styles.mobCtaTextBlock}>
              <Text style={styles.mobCtaTitle}>
                {resolvedInviterName
                  ? `${resolvedInviterName} invited you`
                  : "You've been invited"}
              </Text>
              <Text style={styles.mobCtaSubtitle}>
                One step away from joining this project.
              </Text>
            </Box>
            <TouchableOpacity
              style={styles.mobJoinBtn}
              onPress={handleJoinPress}
              activeOpacity={0.85}>
              <Text style={styles.mobJoinBtnText}>{cta.button}</Text>
            </TouchableOpacity>
          </Box>

          {/* Members card */}
          {members.length > 0 && (
            <Box style={styles.mobCard}>
              <Box style={styles.mobCardTitleRow}>
                <Text style={styles.mobCardTitle}>
                  {usersCount === 1 ? 'Member' : 'Members'} ({usersCount})
                </Text>
              </Box>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.mobMembersRow}>
                {members.slice(0, 8).map((member, index) => {
                  const uri = buildAvatarUri(member.avatar);
                  const initials = getInitials(member.name ?? '');
                  return (
                    <Box key={member.id} style={styles.mobMemberItem}>
                      <Box
                        style={[
                          styles.mobMemberAvatarOuter,
                          { backgroundColor: getAvatarColor(index) },
                        ]}>
                        {uri ? (
                          <Image
                            source={{ uri }}
                            style={styles.mobMemberAvatarImg}
                          />
                        ) : (
                          <Box style={styles.mobMemberAvatarFallback}>
                            <Text style={styles.mobMemberInitials}>
                              {initials}
                            </Text>
                          </Box>
                        )}
                      </Box>
                      <Text style={styles.mobMemberName} numberOfLines={1}>
                        {member.name ?? ''}
                      </Text>
                    </Box>
                  );
                })}
              </ScrollView>
            </Box>
          )}

          {/* Checklist row */}
          <Box style={styles.mobCard}>
            <Box style={styles.mobFeatureRow}>
              <Box style={styles.mobFeatureIconBox}>
                <Icon name='FileText' width={20} height={20} color='white' />
              </Box>
              <Box style={styles.mobFeatureTextBlock}>
                <Text style={styles.mobFeatureTitle}>Checklist</Text>
                <Text style={styles.mobFeatureSubtitle}>
                  Start faster with a project template.
                </Text>
              </Box>
              <Icon name='ChevronRight' width={24} height={24} color='grey04' />
            </Box>
          </Box>

          {/* Tasks row */}
          <Box style={styles.mobCard}>
            <Box style={styles.mobFeatureRow}>
              <Box style={styles.mobFeatureIconBox}>
                <Icon name='TTHashtag' width={20} height={20} color='white' />
              </Box>
              <Box style={styles.mobFeatureTextFlex}>
                <Text style={styles.mobFeatureTitle}>Tasks</Text>
              </Box>
              <Icon name='ChevronRight' width={24} height={24} color='grey04' />
            </Box>
          </Box>

          {/* Activity Log + Files & Media */}
          <Box style={styles.mobTwoColRow}>
            <Box style={[styles.mobCard, styles.mobHalfCard]}>
              <Box style={styles.mobFeatureIconBox}>
                <Icon name='ActivityDot' width={20} height={20} color='white' />
              </Box>
              <Text style={styles.mobFeatureTitle}>Activity Log</Text>
            </Box>
            <Box style={[styles.mobCard, styles.mobHalfCard]}>
              <Box style={styles.mobFeatureIconBox}>
                <Icon name='Image' width={20} height={20} color='white' />
              </Box>
              <Text style={styles.mobFeatureTitle}>Files & Media</Text>
            </Box>
          </Box>

          {/* Login row */}
          <Box style={styles.expiredLoginRow}>
            <Text style={styles.expiredLoginPrompt}>Already on TaskTag? </Text>
            <TouchableOpacity onPress={handleLogin}>
              <Text style={styles.expiredLoginLink}>Log in</Text>
            </TouchableOpacity>
          </Box>
        </ScrollView>

        {signupModal}
      </Box>
    );
  }

  /* ── Desktop 3-panel layout ─────────────────────────────────────────────── */

  const inviteText = resolvedInviterName
    ? `You've been invited to this project by ${resolvedInviterName}`
    : "You've been invited to this project";

  return (
    <Box style={styles.container}>
      <Box style={styles.threePanel}>
        {/* ─── Left sidebar (256px) ─────────────────────────────────────── */}
        <Box style={styles.sidebar}>
          <Box style={styles.sidebarHeader}>
            <Icon name='Logo' width={100} height={28} color='textPrimary' />
            <Box style={styles.sidebarCollapseIcon}>
              <Icon name='ChevronsLeft' variant='l' color='grey04' />
            </Box>
          </Box>

          <Box style={styles.sidebarNav}>
            {/* Projects — active */}
            <Box style={styles.sidebarNavItemActive}>
              <Icon name='FolderActive' width={24} height={24} />
              <Text variant='sidebarNavLabel' color='greenSecondary'>
                Projects
              </Text>
            </Box>
            <DisabledNavItem iconName='TTHashtag' width={73} />
            <DisabledNavItem iconName='ActivityDot' width={104} />
            <DisabledNavItem iconName='User' width={70} />
          </Box>

          <Box style={styles.sidebarFooter}>
            <Box style={styles.sidebarDivider} />
            <Box style={styles.sidebarNavItemFooter}>
              <Icon name='HelpCircle' variant='l' color='grey02' />
              <Box style={[styles.navLabelSkeleton, { width: 70 }]} />
            </Box>
            <Box style={styles.sidebarNavItemFooter}>
              <Icon name='User' variant='l' color='grey02' />
              <Box style={[styles.navLabelSkeleton, { width: 95 }]} />
            </Box>
          </Box>
        </Box>

        {/* ─── Main panel (flex: 1) ─────────────────────────────────────── */}
        <Box style={styles.mainPanel}>
          {/* Header */}
          <Box style={styles.mainHeader}>
            <Box style={styles.mainHeaderInfo}>
              <Text style={styles.mainProjectName} numberOfLines={1}>
                {name}
              </Text>
              {!!address && (
                <Text style={styles.mainProjectAddress} numberOfLines={1}>
                  {address}
                </Text>
              )}
            </Box>
            <Box style={styles.mainHeaderActions}>
              <Box style={styles.iconBtnDisabled}>
                <Icon name='Search' variant='l' color='grey04' />
              </Box>
              <Box style={styles.newTaskBtnDisabled}>
                <Icon name='Plus' variant='s' color='grey04' />
                <Text style={styles.newTaskBtnText}>New Task</Text>
              </Box>
            </Box>
          </Box>

          {/* Description card */}
          {!!description && (
            <Box style={styles.mainDescCard}>
              <Text style={styles.descLabel}>Description</Text>
              <Text style={styles.descBody} numberOfLines={3}>
                {description}
              </Text>
            </Box>
          )}

          {/* Tab bar */}
          <Box style={styles.mainTabBar}>
            <Box style={styles.mainTabBarLeft}>
              <TabItem label='Tasks' iconName='TTHashtag' active />
              <TabItem label='Checklist' iconName='FileText' />
              <TabItem label='Files & Media' iconName='Image' />
              <TabItem label='Activity Log' iconName='ActivityDot' />
              <TabItem label='Members' iconName='Users' badge={usersCount} />
            </Box>
            <Box style={styles.mainTabBarRight}>
              <Box style={styles.iconBtnDisabled}>
                <Icon name='Link' variant='l' color='grey04' />
              </Box>
              <Box style={styles.inviteBtnDisabled}>
                <Icon name='UserPlus' variant='s' color='grey04' />
                <Text style={styles.inviteBtnText}>Invite</Text>
              </Box>
            </Box>
          </Box>

          {/* CTA area — centered, no gap (avoids RN-Web gap rendering bug) */}
          <Box style={styles.mainCtaArea}>
            <Box style={styles.ctaIconWrap}>
              <Icon name='UserPlus' width={40} height={40} color='grey05' />
            </Box>
            <Text style={styles.ctaInviteText}>{inviteText}</Text>
            <TouchableOpacity onPress={handleJoinPress}>
              <Box
                backgroundColor='grey05'
                alignItems='center'
                justifyContent='center'
                style={{
                  borderRadius: theme.borderRadii.l,
                  height: theme.spacing.xxl,
                  minWidth: 180,
                  paddingHorizontal: theme.spacing.xl,
                }}>
                <Text style={styles.joinPillBtnText}>{cta.button}</Text>
              </Box>
            </TouchableOpacity>
          </Box>
        </Box>

        {/* ─── Right chat panel ────────────────────────────────────────── */}
        <Box style={styles.chatPanel}>
          <Box style={styles.chatHeader}>
            <Text style={styles.chatTitle}>Chat</Text>
          </Box>

          {/* Chat item — all text replaced with skeleton rectangles */}
          <Box style={styles.chatListContainer}>
            <Box style={styles.chatItemRow}>
              <Box style={styles.chatAvatarWrap}>
                <Icon name='User' variant='xxl' color='white' />
              </Box>
              <Box style={styles.chatItemBody}>
                <Box style={styles.chatNameSkeleton} />
                <Box style={styles.chatMsgSkeleton} />
              </Box>
              <Box style={styles.chatItemTime}>
                <Box style={styles.chatTimeSkeleton} />
              </Box>
            </Box>
          </Box>

          {/* New Message FAB */}
          <TouchableOpacity
            style={styles.newMsgFab}
            onPress={handleJoinPress}
            activeOpacity={0.85}>
            <Icon name='ChatNew' variant='l' color='white' />
            <Text style={styles.newMsgFabText}>New Message</Text>
          </TouchableOpacity>
        </Box>
      </Box>

      {signupModal}
    </Box>
  );
};

/* ─── styles ──────────────────────────────────────────────────────────────── */

const styles = StyleSheet.create({
  ...sharedStyleDefs,

  /* ── Shared ──────────────────────────────────────────────────────────── */
  container: {
    flex: 1,
    backgroundColor: theme.colors.white,
  },
  topBarSignUp: {
    color: theme.colors.greenSecondary,
  },
  content: {
    padding: theme.spacing.m,
    paddingBottom: theme.spacing.xxl,
  },
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.grey01,
  },

  /* ── Error states ────────────────────────────────────────────────────── */
  errorTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: theme.colors.textPrimary,
    textAlign: 'center',
    marginBottom: theme.spacing.s,
  },
  errorBody: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    lineHeight: 22,
  },
  buttonLabel: {
    color: theme.colors.white,
    fontSize: 16,
    fontWeight: '600',
  },

  /* ── Expired state ───────────────────────────────────────────────────── */
  expiredScrollContent: {
    alignItems: 'center',
    maxWidth: 640,
    alignSelf: 'center',
    width: '100%',
  },
  expiredHeaderBlock: {
    alignItems: 'center',
    marginBottom: theme.spacing.m,
    gap: theme.spacing.s,
  },
  expiredIconCircle: {
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.borderRadii.xxl,
    backgroundColor: theme.colors.alertRedMild,
    marginBottom: theme.spacing.xs,
  },
  expiredIconCircleDesktop: { width: 80, height: 80 },
  expiredIconCircleMobile: { width: 64, height: 64 },
  expiredTitle: {
    fontWeight: '700',
    color: theme.colors.textPrimary,
    textAlign: 'center',
  },
  expiredTitleDesktop: { fontSize: 32 },
  expiredTitleMobile: { fontSize: 28 },
  expiredSubtitle: {
    fontSize: 16,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  expiredProjectCard: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadii.l,
    borderWidth: 1,
    borderColor: theme.colors.grey02,
    padding: theme.spacing.m,
    width: '100%',
    marginBottom: theme.spacing.l,
    gap: theme.spacing.m,
  },
  expiredProjectInfoBlock: {
    backgroundColor: theme.colors.grey01,
    borderRadius: theme.borderRadii.s,
    borderWidth: 1,
    borderColor: theme.colors.grey02,
    padding: theme.spacing.m,
    gap: theme.spacing.s,
  },
  expiredProjectName: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
  expiredProjectAddress: {
    fontSize: 13,
    color: theme.colors.textSecondary,
  },
  expiredProjectMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.m,
    flexWrap: 'wrap',
  },
  expiredMetaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
    minWidth: 0,
  },
  expiredInviterDot: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: theme.colors.greenDark,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  expiredInviterInitials: {
    color: theme.colors.white,
    fontSize: 8,
    fontWeight: '700' as const,
    letterSpacing: 0.2,
  },
  expiredMetaText: {
    fontSize: 13,
    color: theme.colors.textSecondary,
    flex: 1,
  },
  expiredSection: { gap: theme.spacing.xxs },
  expiredSectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
  expiredSectionBody: {
    fontSize: 13,
    color: theme.colors.textSecondary,
    lineHeight: 20,
  },
  expiredMemberRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  expiredTeamAvatar: {
    width: 16,
    height: 16,
    borderRadius: theme.borderRadii.xxs,
  },
  expiredTeamIconBox: {
    width: 16,
    height: 16,
    borderRadius: theme.borderRadii.xxs,
    backgroundColor: theme.colors.black,
    alignItems: 'center',
    justifyContent: 'center',
  },
  expiredJoinButton: {
    backgroundColor: theme.colors.greenSecondary,
    borderRadius: theme.borderRadii.s,
    height: 52,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing.xs,
  },
  expiredJoinButtonLabel: {
    color: theme.colors.white,
    fontSize: 16,
    fontWeight: '600',
  },
  expiredAdminHint: {
    fontSize: 13,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginBottom: theme.spacing.l,
  },
  expiredLoginRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: theme.spacing.l,
    gap: theme.spacing.xxs,
  },
  expiredLoginPrompt: {
    fontSize: 14,
    color: theme.colors.textSecondary,
  },
  expiredLoginLink: {
    fontSize: 14,
    color: theme.colors.greenSecondary,
    fontWeight: '600',
  },

  /* ── Shared description + join pill ─────────────────────────────────── */
  descLabel: {
    fontSize: 16,
    fontWeight: '600',
    lineHeight: 21,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.xs,
  },
  descBody: {
    fontSize: 12,
    fontWeight: '400',
    color: theme.colors.textSecondary,
    lineHeight: 16,
    letterSpacing: 0.02,
  },
  joinPillBtn: {
    backgroundColor: theme.colors.textSecondary,
    borderRadius: 40,
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.m,
    width: 180,
    alignItems: 'center',
  },
  joinPillBtnText: {
    color: theme.colors.white,
    fontSize: 14,
    fontWeight: '500',
  },

  /* ── Mobile valid-project layout ─────────────────────────────────────── */
  mobileContent: {
    padding: theme.spacing.m,
    paddingBottom: 48,
    gap: theme.spacing.s,
  },
  mobileProjectHeader: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadii.s,
    padding: theme.spacing.m,
    borderWidth: 1,
    borderColor: theme.colors.grey02,
  },
  mobileProjectName: {
    fontSize: 20,
    fontWeight: '700',
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.xxs,
  },
  mobileProjectAddress: {
    fontSize: 13,
    color: theme.colors.textSecondary,
  },
  mobileDescCard: {
    backgroundColor: theme.colors.grey01,
    borderRadius: theme.borderRadii.s,
    padding: theme.spacing.m,
  },
  mobileCtaArea: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadii.s,
    padding: theme.spacing.l,
    alignItems: 'center',
    gap: theme.spacing.m,
    borderWidth: 1,
    borderColor: theme.colors.grey02,
  },
  mobileCtaText: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    textAlign: 'center',
    lineHeight: 24,
  },

  /* ── Mobile redesign (Figma) ──────────────────────────────────────────── */
  mobProjectCard: {
    borderRadius: theme.borderRadii.s,
    padding: theme.spacing.m,
  },
  mobProjectTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  mobProjectName: {
    flex: 1,
    fontSize: 18,
    fontWeight: '700',
    color: theme.colors.white,
    lineHeight: 24,
  },
  mobProjectAddressRow: {
    // Align under the title text: icon width (l=24) + title row gap (xs=8).
    paddingLeft: theme.spacing.l + theme.spacing.xs,
    marginTop: theme.spacing.xxs,
  },
  mobProjectAddress: {
    fontSize: 12,
    fontWeight: '400',
    color: theme.colors.white,
    opacity: 0.9,
    lineHeight: 16,
  },
  mobTagsRow: {
    marginTop: theme.spacing.m,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.m,
    flexWrap: 'wrap' as any,
  },
  mobTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  mobTeamLogoBox: {
    width: 24,
    height: 24,
    borderRadius: theme.borderRadii.xxs,
    backgroundColor: theme.colors.black,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobTeamLogoImg: {
    width: 24,
    height: 24,
    borderRadius: theme.borderRadii.xxs,
  },
  mobTagAvatar: {
    width: 24,
    height: 24,
    borderRadius: 12,
  },
  mobTagAvatarFallback: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobTagAvatarInitials: {
    color: theme.colors.white,
    fontSize: 9,
    fontWeight: '700' as const,
  },
  mobTagText: {
    color: theme.colors.white,
    fontSize: 12,
    lineHeight: 16,
  },
  mobCard: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadii.s,
    padding: theme.spacing.m,
  },
  mobCardTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.s,
  },
  mobCardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.black,
    lineHeight: 21,
  },
  mobSeeMore: {
    fontSize: 12,
    color: theme.colors.grey04,
    lineHeight: 16,
  },
  mobCtaCard: {
    backgroundColor: theme.colors.black,
    borderRadius: theme.borderRadii.s,
    padding: theme.spacing.m,
    gap: theme.spacing.s,
    alignItems: 'center',
  },
  mobCtaAvatarWrap: {
    width: 32,
    height: 32,
    borderRadius: 16,
    overflow: 'hidden' as any,
  },
  mobCtaAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  mobCtaAvatarFallback: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: theme.colors.transparentGreenDark,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobCtaTextBlock: {
    alignItems: 'center',
    gap: theme.spacing.xxs,
  },
  mobCtaTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.white,
    lineHeight: 21,
    textAlign: 'center',
  },
  mobCtaSubtitle: {
    fontSize: 12,
    color: theme.colors.white,
    lineHeight: 16,
    letterSpacing: 0.24,
    textAlign: 'center',
  },
  mobJoinBtn: {
    backgroundColor: theme.colors.greenSecondary,
    borderRadius: theme.borderRadii.s,
    height: 52,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing.xs,
  },
  mobJoinBtnText: {
    color: theme.colors.white,
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 16,
  },
  mobMembersRow: {
    flexDirection: 'row',
    gap: theme.spacing.m,
    paddingTop: theme.spacing.xxs,
  },
  mobMemberItem: {
    alignItems: 'center',
    width: 56,
    gap: theme.spacing.xxs,
  },
  mobMemberAvatarOuter: {
    width: 56,
    height: 56,
    borderRadius: 28,
    overflow: 'hidden' as any,
  },
  mobMemberAvatarImg: {
    width: 56,
    height: 56,
    borderRadius: 28,
  },
  mobMemberAvatarFallback: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobMemberInitials: {
    color: theme.colors.white,
    fontSize: 16,
    fontWeight: '600',
  },
  mobMemberName: {
    fontSize: 12,
    color: theme.colors.grey04,
    lineHeight: 16,
    textAlign: 'center',
  },
  mobFeatureRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.s,
  },
  mobFeatureIconBox: {
    width: 32,
    height: 32,
    borderRadius: theme.borderRadii.xs,
    backgroundColor: theme.colors.grey04,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobFeatureTextBlock: {
    flex: 1,
    gap: theme.spacing.xxs,
  },
  mobFeatureTextFlex: {
    flex: 1,
  },
  mobFeatureTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.grey04,
    lineHeight: 21,
  },
  mobFeatureSubtitle: {
    fontSize: 12,
    color: theme.colors.grey04,
    lineHeight: 16,
  },
  mobTwoColRow: {
    flexDirection: 'row',
    gap: theme.spacing.s,
  },
  mobHalfCard: {
    flex: 1,
    gap: theme.spacing.s,
  },

  /* ── Desktop 3-panel ─────────────────────────────────────────────────── */
  threePanel: {
    flex: 1,
    flexDirection: 'row',
    overflow: 'hidden' as any,
  },

  /* Sidebar */
  sidebar: {
    width: 256,
    backgroundColor: theme.colors.grey01,
    borderRightWidth: 1,
    borderRightColor: theme.colors.grey02,
    flexDirection: 'column',
  },
  sidebarHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.l,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.grey02,
  },
  sidebarCollapseIcon: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sidebarNav: {
    flex: 1,
    paddingTop: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
    gap: theme.spacing.xs,
  },
  /* Active nav item */
  sidebarNavItemActive: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.m,
    paddingVertical: 15,
    borderRadius: theme.borderRadii.xs,
    gap: theme.spacing.m,
    backgroundColor: theme.colors.greenSecondaryMild,
  },
  /* Inactive nav item */
  sidebarNavItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.m,
    paddingVertical: 15,
    borderRadius: theme.borderRadii.xs,
    gap: theme.spacing.m,
  },
  navLabelSkeleton: {
    height: 14,
    borderRadius: 7,
    backgroundColor: theme.colors.grey02,
    width: 80,
  },
  sidebarFooter: {
    paddingHorizontal: theme.spacing.m,
    paddingBottom: theme.spacing.m,
    gap: theme.spacing.xs,
  },
  sidebarDivider: {
    height: 1,
    backgroundColor: theme.colors.grey02,
    marginBottom: theme.spacing.xs,
  },
  sidebarNavItemFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.m,
    paddingVertical: 15,
    borderRadius: theme.borderRadii.xs,
    gap: theme.spacing.m,
  },
  profileAvatarCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: theme.colors.grey02,
    alignItems: 'center',
    justifyContent: 'center',
  },

  /* Main panel */
  mainPanel: {
    flex: 1,
    backgroundColor: theme.colors.white,
    flexDirection: 'column',
  },
  mainHeader: {
    height: 74,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.m,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.grey02,
    flexShrink: 0,
  },
  mainHeaderInfo: {
    flex: 1,
    paddingRight: theme.spacing.m,
  },
  mainProjectName: {
    fontSize: 22,
    fontWeight: '600',
    color: theme.colors.black,
    lineHeight: 32,
  },
  mainProjectAddress: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    opacity: 0.8,
    marginTop: 2,
  },
  mainHeaderActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.m,
  },
  iconBtnDisabled: {
    width: 32,
    height: 32,
    borderRadius: theme.borderRadii.xs,
    alignItems: 'center',
    justifyContent: 'center',
  },
  newTaskBtnDisabled: {
    backgroundColor: theme.colors.grey02,
    borderRadius: 40,
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.xs,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  newTaskBtnText: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 16,
    color: theme.colors.grey04,
  },

  /* Description card in main panel */
  mainDescCard: {
    backgroundColor: theme.colors.grey01,
    marginHorizontal: theme.spacing.m,
    marginTop: theme.spacing.m,
    padding: theme.spacing.m,
    borderRadius: theme.borderRadii.xs,
    flexShrink: 0,
  },

  /* Tab bar */
  mainTabBar: {
    flexDirection: 'row',
    alignItems: 'stretch',
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.grey02,
    paddingHorizontal: theme.spacing.m,
    flexShrink: 0,
    overflow: 'hidden' as any,
  },
  mainTabBarLeft: {
    flexDirection: 'row',
    alignItems: 'stretch',
    flex: 1,
    overflow: 'hidden' as any,
  },
  mainTabBarRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.m,
    paddingVertical: theme.spacing.s,
  },
  inviteBtnDisabled: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.xxs,
    borderWidth: 1,
    borderColor: theme.colors.grey04,
    borderRadius: 40,
    paddingHorizontal: theme.spacing.s,
    paddingVertical: 6,
    width: 122,
    height: 32,
  },
  inviteBtnText: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 16,
    color: theme.colors.grey04,
    marginLeft: theme.spacing.xs,
  },

  /* TabItem sub-component styles */
  tabItemWrap: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-end',
    minWidth: 96,
    paddingTop: theme.spacing.xs,
  },
  tabItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: 32,
    gap: theme.spacing.xs,
    paddingHorizontal: theme.spacing.xs,
    flex: 1,
  },
  tabItemText: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 16,
    color: theme.colors.grey04,
  },
  tabBadge: {
    backgroundColor: theme.colors.grey04,
    borderRadius: 200,
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tabBadgeText: {
    fontSize: 12,
    color: theme.colors.white,
    fontWeight: '400',
  },
  tabUnderbar: {
    height: 1,
    backgroundColor: theme.colors.grey04,
    width: '100%',
    marginTop: theme.spacing.xs,
  },
  tabUnderbarHidden: {
    opacity: 0,
  },

  /* CTA area — uses marginBottom instead of gap to avoid RN-Web rendering issue */
  mainCtaArea: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.xxl,
  },
  ctaIconWrap: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing.xs,
  },
  ctaInviteText: {
    fontSize: 22,
    fontWeight: '600',
    color: theme.colors.textSecondary,
    textAlign: 'center',
    lineHeight: 32,
    marginBottom: theme.spacing.m,
  },

  /* Chat panel */
  chatPanel: {
    width: 550,
    backgroundColor: theme.colors.white,
    borderLeftWidth: 1,
    borderLeftColor: theme.colors.grey02,
    flexDirection: 'column',
  },
  chatHeader: {
    height: 74,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.grey02,
    paddingHorizontal: theme.spacing.l,
    justifyContent: 'center',
    flexShrink: 0,
  },
  chatTitle: {
    fontSize: 22,
    fontWeight: '600',
    color: theme.colors.black,
  },
  chatListContainer: {
    paddingHorizontal: theme.spacing.xs,
    position: 'relative',
  },
  chatItemRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: theme.spacing.m,
    paddingVertical: 10,
  },
  chatAvatarWrap: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: theme.colors.orange,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginRight: theme.spacing.xxs,
  },
  chatItemBody: {
    flex: 1,
    justifyContent: 'center',
    gap: theme.spacing.xxs,
    paddingRight: theme.spacing.s,
  },
  chatNameSkeleton: {
    height: 14,
    borderRadius: 7,
    backgroundColor: theme.colors.grey02,
    width: 120,
    marginTop: theme.spacing.xxs,
    marginBottom: theme.spacing.xs,
  },
  chatMsgSkeleton: {
    height: 14,
    borderRadius: 7,
    backgroundColor: theme.colors.grey02,
    alignSelf: 'stretch',
    marginBottom: theme.spacing.xxs,
  },
  chatItemTime: {
    height: 48,
    justifyContent: 'flex-end',
    // paddingRight: theme.spacing.s,
    paddingBottom: theme.spacing.xxs,
  },
  chatTimeSkeleton: {
    height: 14,
    borderRadius: 5,
    backgroundColor: theme.colors.grey02,
    width: 40,
    marginTop: 4,
    flexShrink: 0,
  },
  /* New Message FAB */
  newMsgFab: {
    position: 'absolute',
    bottom: 16,
    right: 16,
    backgroundColor: theme.colors.black,
    borderRadius: 156,
    paddingHorizontal: theme.spacing.s,
    paddingVertical: theme.spacing.s,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  newMsgFabText: {
    color: theme.colors.white,
    fontSize: 14,
    fontWeight: '500',
  },
});

export default ProjectInvitationPreviewScreenWeb;
